import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TEMPERAMENTS,
  fixedToSpayed,
  type AnimalType,
  type ColorKey,
  type FixedOption,
  type Gender,
  type Temperament,
} from '../lib/constants'
import {
  createLostReport,
  uploadAnimalImage,
  type LostListItem,
} from '../lib/lostApi'
import { prepareImagesForUpload } from '../lib/prepareImage'
import { resolvePostCreateNotifications } from '../lib/pushNotifications'
import {
  AnimalReportFields,
  type ChipStatus,
} from './AnimalReportFields'
import { AnimalTypePicker } from './AnimalTypePicker'
import { NotificationOptInPrompt } from './NotificationOptInPrompt'
import { PhotoPicker } from './PhotoPicker'
import { SightingDateField, SightingTimeField } from './SightingDateTimePickers'
import type { SightingLocation } from './AddSightingModal'

interface Props {
  open: boolean
  onClose: () => void
  pickedMapLocation: SightingLocation | null
  onRequestMapPick: () => void
  onSubmitted: (item: LostListItem) => void
}

function localDateValue(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

function localTimeValue(date = new Date()) {
  return date.toTimeString().slice(0, 5)
}

function formatCoordinates(location: SightingLocation) {
  return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
}

function toMissingAt(date: string, time: string) {
  return new Date(`${date}T${time}:00`).toISOString()
}

export function ReportLostModal({
  open,
  onClose,
  pickedMapLocation,
  onRequestMapPick,
  onSubmitted,
}: Props) {
  const { t, i18n } = useTranslation()
  const [type, setType] = useState<AnimalType | ''>('')
  const [color1, setColor1] = useState('')
  const [color2, setColor2] = useState('')
  const [color3, setColor3] = useState('')
  const [chipStatus, setChipStatus] = useState<ChipStatus>('unknown')
  const [chip, setChip] = useState('')
  const [gender, setGender] = useState<Gender>('unknown')
  const [fixed, setFixed] = useState<FixedOption>('unknown')
  const [temperament, setTemperament] = useState<Temperament>('not_sure')
  const [images, setImages] = useState<File[]>([])
  const [breedOther, setBreedOther] = useState('')
  const [contact, setContact] = useState('')
  const [showTypeError, setShowTypeError] = useState(false)
  const [dateMode, setDateMode] = useState<'today' | 'custom'>('today')
  const [selectedDate, setSelectedDate] = useState(localDateValue)
  const [timeMode, setTimeMode] = useState<'now' | 'custom'>('now')
  const [selectedTime, setSelectedTime] = useState(localTimeValue)
  const [locationMode, setLocationMode] = useState<'current' | 'map' | null>(null)
  const [location, setLocation] = useState<SightingLocation | null>(null)
  const [locationPending, setLocationPending] = useState(false)
  const [locationError, setLocationError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [notifyPrompt, setNotifyPrompt] = useState<{
    id: number
    type: AnimalType
  } | null>(null)

  const today = localDateValue()
  const isDifferentDate = selectedDate !== today

  useEffect(() => {
    if (!pickedMapLocation) return
    setLocation(pickedMapLocation)
    setLocationMode('map')
    setLocationError(false)
  }, [pickedMapLocation])

  useEffect(() => {
    if (!open) return
    if (dateMode === 'today') setSelectedDate(localDateValue())
    if (timeMode === 'now') setSelectedTime(localTimeValue())
  }, [open, dateMode, timeMode])

  function resetForm() {
    setType('')
    setColor1('')
    setColor2('')
    setColor3('')
    setChipStatus('unknown')
    setChip('')
    setGender('unknown')
    setFixed('unknown')
    setTemperament('not_sure')
    setImages([])
    setBreedOther('')
    setContact('')
    setShowTypeError(false)
    setDateMode('today')
    setSelectedDate(localDateValue())
    setTimeMode('now')
    setSelectedTime(localTimeValue())
    setLocationMode(null)
    setLocation(null)
    setLocationPending(false)
    setLocationError(false)
    setSubmitting(false)
    setSubmitError(null)
    setNotifyPrompt(null)
  }

  function chooseCurrentLocation() {
    setLocationPending(true)
    setLocationError(false)
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        setLocation({ lat: coords.latitude, lng: coords.longitude })
        setLocationMode('current')
        setLocationPending(false)
      },
      () => {
        setLocationError(true)
        setLocationPending(false)
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
    if (!navigator.geolocation) {
      setLocationError(true)
      setLocationPending(false)
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (submitting) return

    const missingType = !type
    setShowTypeError(missingType)
    if (missingType || !location || !color1 || !contact.trim()) {
      if (!location) setLocationError(true)
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      const prepared =
        images.length > 0 ? await prepareImagesForUpload(images) : []
      const created = await createLostReport({
        type,
        color1: color1 as ColorKey,
        color2: (color2 || null) as ColorKey | null,
        color3: (color3 || null) as ColorKey | null,
        chip_status: chipStatus,
        chip: chipStatus === 'known' ? chip : null,
        gender,
        spayed: fixedToSpayed(fixed),
        breed_other: breedOther.trim() || null,
        contact_info: contact.trim(),
        temperament,
        missing_noticed_at: toMissingAt(selectedDate, selectedTime),
        last_known_lat: location.lat,
        last_known_lng: location.lng,
        source_locale: i18n.resolvedLanguage || i18n.language || 'en',
      })

      if (prepared.length > 0) {
        try {
          for (const image of prepared) {
            await uploadAnimalImage(created.animal_id, image)
          }
        } catch (photoError) {
          onSubmitted({
            ...created,
            thumbnail_key: null,
          })
          setSubmitError(
            photoError instanceof Error && photoError.message
              ? `${t('forms.photoUploadFailed')} ${photoError.message}`
              : t('forms.photoUploadFailed'),
          )
          setSubmitting(false)
          return
        }
      }

      onSubmitted({
        ...created,
        thumbnail_key: null,
      })
      const notifyOutcome = await resolvePostCreateNotifications({
        kind: 'lost',
        id: created.id,
      })
      setSubmitting(false)
      if (notifyOutcome === 'prompt') {
        setNotifyPrompt({ id: created.id, type: created.animal.type })
      } else {
        resetForm()
        onClose()
      }
    } catch (caught) {
      setSubmitError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('status.error'),
      )
      setSubmitting(false)
    }
  }

  if (!open) return null

  if (notifyPrompt) {
    return (
      <NotificationOptInPrompt
        kind="lost"
        reportId={notifyPrompt.id}
        animalType={notifyPrompt.type}
        onDone={() => {
          setNotifyPrompt(null)
          resetForm()
          onClose()
        }}
        onSkip={() => {
          setNotifyPrompt(null)
          resetForm()
          onClose()
        }}
      />
    )
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal-wide sighting-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lost-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="lost-title">{t('forms.reportLostTitle')}</h2>
        <form className="sighting-form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="sighting-form-columns">
            <div className="sighting-form-column">
              <div className="section-heading">
                <span>01</span>
                <h3>{t('forms.animalDetails')}</h3>
              </div>
              <AnimalTypePicker
                idPrefix="lost"
                value={type}
                onChange={(nextType) => {
                  setType(nextType)
                  setShowTypeError(false)
                }}
                showRequiredError={showTypeError}
              />
              <AnimalReportFields
                idPrefix="lost"
                color1={color1}
                color2={color2}
                color3={color3}
                gender={gender}
                fixed={fixed}
                chipStatus={chipStatus}
                chip={chip}
                chipLabelContext="owner"
                onColor1={setColor1}
                onColor2={setColor2}
                onColor3={setColor3}
                onGender={setGender}
                onFixed={setFixed}
                onChipStatus={(status) => {
                  setChipStatus(status)
                  if (status !== 'known') setChip('')
                }}
                onChip={setChip}
              />
              <div className="field">
                <label htmlFor="lost-temperament">{t('fields.temperament')}</label>
                <select
                  id="lost-temperament"
                  value={temperament}
                  onChange={(event) =>
                    setTemperament(event.target.value as Temperament)
                  }
                >
                  {TEMPERAMENTS.map((key) => (
                    <option key={key} value={key}>
                      {t(`temperament.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="breed-other">
                  {t('fields.breed')} ({t('fields.other')})
                </label>
                <input
                  id="breed-other"
                  value={breedOther}
                  onChange={(e) => setBreedOther(e.target.value)}
                  placeholder={t('forms.breedOtherHint')}
                />
              </div>
              <div className="field">
                <label htmlFor="contact">{t('fields.contact')}</label>
                <textarea
                  id="contact"
                  rows={3}
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="sighting-form-column">
              <div className="section-heading">
                <span>02</span>
                <h3>{t('forms.whenAndWhere')}</h3>
              </div>
              <p className="field-help">{t('lost.missingWhenHelp')}</p>
              <SightingDateField
                dateMode={dateMode}
                selectedDate={selectedDate}
                today={today}
                onSelectToday={() => {
                  setDateMode('today')
                  setSelectedDate(localDateValue())
                }}
                onSelectDate={(value) => {
                  setDateMode('custom')
                  setSelectedDate(value)
                }}
              />
              <SightingTimeField
                timeMode={timeMode}
                selectedTime={selectedTime}
                disabledNow={isDifferentDate}
                onSelectNow={() => {
                  setTimeMode('now')
                  setSelectedTime(localTimeValue())
                }}
                onSelectTime={(value) => {
                  setTimeMode('custom')
                  setSelectedTime(value)
                }}
              />

              <fieldset className="report-section">
                <legend>{t('lost.lastKnownLocation')}</legend>
                <div className="choice-pair">
                  <button
                    type="button"
                    className={`choice-button${locationMode === 'current' ? ' selected' : ''}`}
                    onClick={chooseCurrentLocation}
                    disabled={locationPending}
                  >
                    {locationMode === 'current' && <span aria-hidden="true">✓</span>}
                    {locationPending
                      ? t('sighting.findingLocation')
                      : t('sighting.currentLocation')}
                  </button>
                  <button
                    type="button"
                    className={`choice-button${locationMode === 'map' ? ' selected' : ''}`}
                    onClick={onRequestMapPick}
                  >
                    {locationMode === 'map' && <span aria-hidden="true">✓</span>}
                    {locationMode === 'map' && location
                      ? formatCoordinates(location)
                      : t('sighting.pickOnMap')}
                  </button>
                </div>
                {locationError && (
                  <p className="field-error" role="alert">
                    {t('lost.locationRequired')}
                  </p>
                )}
              </fieldset>

              <PhotoPicker idPrefix="lost" images={images} onImages={setImages} />
            </div>
          </div>

          {submitError && (
            <p className="field-error" role="alert">
              {submitError}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('status.submitting') : t('actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
